if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      static STORAGE_KEY = 'Concept:recent-searches';
      static MAX_RECENT = 6;

      constructor() {
        super();

        this.cachedMap = new Map();
        this.focusElement = this.input;
        this.selectedIndex = -1;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
        this.input.addEventListener('keydown', this.onKeydown.bind(this));
        this.addEventListener('submit', this.onSubmit.bind(this));

        this.initRecentSearches();
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      get recentContainer() {
        return this.querySelector('[data-recent-searches]');
      }

      get recentList() {
        return this.querySelector('[data-recent-list]');
      }

      get clearRecentButton() {
        return this.querySelector('[data-clear-recent]');
      }

      initRecentSearches() {
        if (this.clearRecentButton) {
          this.clearRecentButton.addEventListener('click', () => {
            this.clearRecentSearches();
          });
        }

        if (this.getQuery().length === 0) {
          this.showRecentSearches();
        }
      }

      getRecentSearches() {
        try {
          return JSON.parse(localStorage.getItem(PredictiveSearch.STORAGE_KEY) || '[]');
        } catch { return []; }
      }

      saveRecentSearch(term) {
        if (!term || term.length < 2) return;
        try {
          let recent = this.getRecentSearches();
          recent = recent.filter(t => t.toLowerCase() !== term.toLowerCase());
          recent.unshift(term);
          recent = recent.slice(0, PredictiveSearch.MAX_RECENT);
          localStorage.setItem(PredictiveSearch.STORAGE_KEY, JSON.stringify(recent));
        } catch { /* private browsing */ }
      }

      clearRecentSearches() {
        try { localStorage.removeItem(PredictiveSearch.STORAGE_KEY); } catch {}
        if (this.recentContainer) this.recentContainer.hidden = true;
      }

      showRecentSearches() {
        const recent = this.getRecentSearches();
        if (!this.recentContainer || !this.recentList || recent.length === 0) return;

        this.recentList.innerHTML = recent.map(term => `
          <li>
            <a class="reversed-link text-base md:text-lg leading-tight font-medium flex items-center gap-3"
               href="${theme.routes.search_url}?q=${encodeURIComponent(term)}"
               data-recent-term="${term.replace(/"/g, '&quot;')}">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="shrink-0 text-opacity" aria-hidden="true"><path d="M12 8v4l3 3"/><circle cx="12" cy="12" r="9"/></svg>
              ${term}
            </a>
          </li>
        `).join('');

        this.recentList.querySelectorAll('[data-recent-term]').forEach(link => {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            const term = link.getAttribute('data-recent-term');
            this.input.value = term;
            this.hideTypewriter();
            this.onChange();
          });
        });

        this.recentContainer.hidden = false;
      }

      hideRecentSearches() {
        if (this.recentContainer) this.recentContainer.hidden = true;
      }

      onSubmit() {
        const query = this.getQuery();
        if (query.length > 0) this.saveRecentSearch(query);
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.getQuery().length === 0) {
          this.showRecentSearches();
        }

        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      onKeydown(e) {
        const links = this.querySelectorAll('.search__results a, .search__recent a, .search__recommendation a');
        if (links.length === 0) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.selectedIndex = Math.min(this.selectedIndex + 1, links.length - 1);
          this.updateSelection(links);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
          this.updateSelection(links);
        } else if (e.key === 'Enter' && this.selectedIndex >= 0) {
          e.preventDefault();
          this.saveRecentSearch(this.getQuery());
          links[this.selectedIndex].click();
        }
      }

      updateSelection(links) {
        links.forEach((link, i) => {
          link.classList.toggle('search__selected', i === this.selectedIndex);
          link.setAttribute('aria-selected', i === this.selectedIndex ? 'true' : 'false');
        });
        if (this.selectedIndex >= 0) {
          links[this.selectedIndex].scrollIntoView({ block: 'nearest' });
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
        this.selectedIndex = -1;
        this.showRecentSearches();
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onChange() {
        this.selectedIndex = -1;

        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();
        this.hideRecentSearches();

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);

            this.removeAttribute('loading');
            this.setAttribute('results', '');

            this.saveRecentSearch(this.getQuery());
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(id).innerHTML;

        if (theme.a11y && theme.a11y.announce) {
          const count = document.getElementById(id).querySelectorAll('a').length;
          theme.a11y.announce(`${count} search results available`);
        }
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
